import React, { useState, useEffect, useCallback } from "react";
import SearchBar from "./SearchBar";
import SearchAssistant from "./SearchAssistant";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { FormattedMessage, injectIntl } from "react-intl";
import ReactGA from "react-ga4";
import { xor } from "lodash";
import { scrollTop } from "../../utils/common";

import { getSearchParams } from "../../utils/params";
import { getApiClient } from "../../utils/Api";

import ResultsList from "./ResultsList";
import SearchViewToggle from "../../components/SearchViewToggle";
import Spaces from "./Spaces";
import { Category, Heading3, Loading, Icon, Button, Container } from "../../components";
import UserNav from "../../components/UserNav";
import PlacementSlot from "../../components/PlacementSlot";
import searchImage from "../../assets/search-icon.png";

import { DEFAULT_SPACES } from "./Spaces";

export const isSameLocation = (l0, l1) => l0 && l1 && l0.lat === l1.lat && l0.lng === l1.lng;

const MOST_USED = [
  { id: 1, name: "Baby", color: "#b30f38" },
  { id: 83, name: "Beratung", color: "#515c0e" },
  { id: 101, name: "Familie", color: "#8d2977" },
  { id: 12, name: "Geburtsvorbereitung", color: "#2f329b" },
  { id: 96, name: "Säugling", color: "#35202d" },
  { id: 14, name: "Yoga", color: "#772458" },
  { id: 85, name: "Workshop", color: "#105f69" },
  { id: 77, name: "Gesundheit", color: "#138070" },
  { id: 100, name: "Ernährungsberatung", color: "#bf6f1d" },
  { id: 79, name: "Beikostberatung", color: "#461dbf" },
  { id: 69, name: "Trageberatung", color: "#797604" },
  { id: 29, name: "Rückbildung", color: "#371a94" },
  { id: 55, name: "Tanzen", color: "#3c551a" },
  { id: 42, name: "Erziehungsbegleitung", color: "#4f5b1c" },
  { id: 70, name: "Stillberatung", color: "#40760e" },
  { id: 112, name: "Beckenboden", color: "#bf731d" },
  { id: 59, name: "Pilates", color: "#412987" },
  { id: 33, name: "Hebammen", color: "#2c3d8d" },
  { id: 123, name: "Babyschlaf", color: "#561029" },
  { id: 120, name: "Stoffwindelberatung", color: "#201dbf" },
  { id: 6, name: "Kreativität", color: "#b53759" },
];

const MAX_PRICE = 500;

const Wrapper = styled.div`
  min-width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
`;

const SearchWrapper = styled.div`
  position: relative;
  min-width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;

  ${props =>
    props.hasTakeover &&
    `@media (${props.theme.tabletScreen}) {
      z-index: 1;
      min-width: auto;
      width: 100%;
      max-width: 1200px;
      margin: 200px auto 0;
      border-radius: ${props.theme.borderRadius};
      background-color: rgba(255, 255, 255, 0.9);
    }`}

  @media (${props => props.theme.tabletScreen}) {
  }

  @media (${props => props.theme.desktopScreen}) {
  }
`;

const NoResults = styled.p`
  padding: 1rem;
  background-color: ${props => props.theme.white};
  box-shadow: ${props => props.theme.boxShadowLifted};
  border-radius: ${props => props.theme.borderRadius};
  max-width: 640px;
  text-align: center;
  margin: 2rem auto;
  width: 90%;
  color: ${props => props.theme.dark};
`;

const LoadingMore = styled(NoResults)`
  background-color: transparent;
  box-shadow: none;
  margin-bottom: 1rem;
  max-width: 180px;

  & > div {
    border-radius: 4rem;
    padding: 0;
  }
`;

const Intro = styled.div`
  padding: 0.5rem;
  text-align: center;
  color: ${props => props.theme.dark};
  max-width: 640px;
  margin: 0 auto 1rem;

  ${Heading3} {
    color: inherit;
  }

  @media (${props => props.theme.tabletScreen}) {
    max-width: 800px;
  }
`;

const SearchIcon = styled.img`
  width: 100%;
  height: auto;
  max-width: 100px;
  min-width: 60px;
  display: inline-block;
  margin: 0.25rem auto;

  @media (${props => props.theme.tabletScreen}) {
    max-width: 200px;
  }
`;

const Categories = styled.div`
  max-width: 480px;
  margin: 0 auto;

  & > div {
    margin: 0.125rem;
    font-size: ${props => props.theme.fontSizes.small};
    padding: 2px 6px;
    &:nth-of-type(1n + 10) {
      display: none;
    }
  }

  @media (${props => props.theme.tabletScreen}) {
    max-width: 100%;
    margin-top: 1rem;
    margin-bottom: 1rem;
    padding: 0.5rem;

    & > div {
      margin: 0.25rem;
      font-size: ${props => props.theme.fontSizes.default};
      padding: 4px 12px;
      &:nth-of-type(1n) {
        display: inline-block;
      }
    }
  }
`;


const SpacesWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 4;

  background-color: ${props => props.theme.light};
  @media (${props => props.theme.tabletScreen}) {
    background-color: transparent;
  }
`;

const ActionsWrapper = styled.p`
  margin-top: 1rem;
`;

const SearchOnline = ({
  term,
  searchCourses,
  searchByText,
  isLoading,
  results,
  metadata,
  intl,
  center,
  address,
  changeAddress,
  clearAddress,
  filters,
  setFilters,
  setFilter,
}) => {
  const params = getSearchParams() || {};

  const [stateTerm, setStateTerm] = useState(params.term || term || "");
  const [page, setPage] = useState(1);
  const [editFilters, setEditFilters] = useState(false);
  const [placements, setPlacements] = useState({});
  const assistedSearch = params.assistant === "1";
  const categorySpaces = filters.categorySpaces;

  const onSearch = useCallback(
    stateTerm => {
      searchCourses(stateTerm, page, center, filters);
    },
    [searchCourses, page, center, filters],
  );

  const nextPage = () => {
    console.log("next page");
    if (!metadata || metadata.totalCount <= metadata.page) {
      console.log("no metadata or no page", metadata);
      return;
    }

    if (isLoading) {
      console.log("no next page while loading pls");
      return;
    }

    setPage(p => p + 1);
  };

  // Trigger new search
  const onCategoryClick = ({ name }) => {
    if (!name) {
      return;
    }

    setPage(1);
    setStateTerm(name);
    setEditFilters(false);

    ReactGA.event({
      category: "Category",
      action: name || "",
    });
  };

  // Initial set of center after incoming search request by params
  useEffect(() => {
    const params = getSearchParams() || {};
    const { address, lat, lng, space } = params;

    // Overwrite address if given by URL params
    if (parseFloat(lat) && parseFloat(lng)) {
      changeAddress(address, parseFloat(lat), parseFloat(lng));
    }

    // Overwrite cateogry spaces if given via URL params
    // e.g. ?space=creative
    if (space) {
      setFilter({ categorySpaces: [space] });
    }
  }, [changeAddress, setFilter]);

  // Search on Term change
  useEffect(() => {
    if (assistedSearch && !stateTerm) {
      return;
    }

    onSearch(stateTerm);
  }, [stateTerm, onSearch, assistedSearch]);

  // Placements belong to page 1 of a search, so loading more results keeps the shown ones
  useEffect(() => {
    if (assistedSearch && !stateTerm) {
      return;
    }

    const placementFilters = {
      ...(center ? { lat: center.lat, lng: center.lng } : {}),
      ...(categorySpaces && categorySpaces.length ? { categorySpaces: categorySpaces.join(",") } : {}),
    };
    getApiClient()
      .placements(stateTerm, placementFilters)
      .then(result => result && setPlacements(result.placements || {}))
      .catch(() => setPlacements({}));
  }, [stateTerm, center, categorySpaces, assistedSearch]);

  useEffect(() => {
    if (!metadata) {
      console.log("page reset because no metadata");
      return setPage(1);
    }

    console.log("page to ", metadata.page);

    setPage(metadata.page);
    return () => {
      console.log("page reset while cleanup");
      setPage(1);
    };
  }, [metadata]);

  // A new address was selected from auto complete field
  const onAddressSelect = (address, lat, lng) => {
    changeAddress(address, lat, lng);
    setEditFilters(false);
  };

  const onSpaceClick = space => {
    updateFilters("categorySpaces", [space.key]);
  };

  const resetSpaces = () => {
    setFilters({
      ...filters,
      categorySpaces: [],
    });
  };

  const onMaxPriceChange = value => {
    setFilter({
      maxPrice: value >= MAX_PRICE ? null : value,
    });
  };

  const onRadiusChange = radius => {
    setFilter({ radius });
    setPage(1);
  };

  const noResults = results.length < 1 && metadata && !isLoading;
  const moreResults = metadata && metadata.totalCount > results.length;
  const spaces = filters.categorySpaces || [];

  const updateFilters = (key, value) => {
    setFilters({
      ...filters,
      [key]: xor(filters[key], value),
    });

    // Reset page when filters changed
    setPage(1);
    scrollTop();
  };

  const onSubmit = term => {
    setPage(1);
    setStateTerm(term);
  };

  return (
    <Wrapper>
      <UserNav />
      {!assistedSearch && (
        <SpacesWrapper>
          <Spaces
            spaces={DEFAULT_SPACES.map(s => ({
              ...s,
              active: spaces.includes(s.key),
            }))}
            onClick={onSpaceClick}
          />
        </SpacesWrapper>
      )}
      {placements.takeover && <PlacementSlot type="takeover" placement={placements.takeover} />}
      <SearchWrapper hasTakeover={!!placements.takeover}>
        <SearchViewToggle active="list" floating />
        {(!metadata || noResults) && !assistedSearch && (
          <Intro>
            <SearchIcon src={searchImage} />
            <Heading3>
              <FormattedMessage id="pages.Search.title" />
            </Heading3>
            {false && (
              <section>
                <Categories>
                  {MOST_USED.map(category => (
                    <Category onClick={onCategoryClick} key={category.name} category={category} />
                  ))}
                </Categories>
              </section>
            )}
          </Intro>
        )}
        {assistedSearch ? (
          <SearchAssistant
            isLoading={isLoading}
            onSubmit={onSubmit}
            searchByText={searchByText}
            filters={filters}
            intl={intl}
            term={stateTerm || term}
            setFilters={setFilters}
            changeAddress={changeAddress}
            address={address}
          />
        ) : (
          <SearchBar
            stateTerm={stateTerm}
            onSubmit={onSubmit}
            isLoading={isLoading}
            editFilters={editFilters}
            setEditFilters={setEditFilters}
            totalCount={metadata?.totalCount || results.length}
            filters={filters}
            setFilters={setFilters}
            center={center}
            address={address}
            onAddressSelect={onAddressSelect}
            onAddressClear={clearAddress}
            onMaxPriceChange={onMaxPriceChange}
            onRadiusChange={onRadiusChange}
            updateFilters={updateFilters}
          />
        )}

        {placements.banner && (
          <Container>
            <PlacementSlot type="banner" placement={placements.banner} />
          </Container>
        )}
        <ResultsList
          results={results}
          onCategoryClick={category => {
            if (
              !window.confirm(
                intl.formatMessage(
                  { id: "pages.SearchMap.searchCategory" },
                  { category: category.name },
                ),
              )
            ) {
              return;
            }
            onCategoryClick(category);
          }}
          hasMore={false}
          term={term}
          skyscraper={
            placements.skyscraper && (
              <PlacementSlot type="skyscraper" placement={placements.skyscraper} />
            )
          }
        />
        {moreResults && (
          <LoadingMore as="div">
            {isLoading ? (
              <Loading />
            ) : (
              <Button onClick={nextPage}>
                <FormattedMessage id="pages.Search.loadMore" />
                <br />
                <Icon name="chevron-up" direction="down" />
              </Button>
            )}
          </LoadingMore>
        )}
        {noResults && (
          <NoResults>
            <FormattedMessage id="pages.Search.noResults" />
            {spaces.length > 0 && (
              <ActionsWrapper>
                <Button onClick={resetSpaces} color="info" outline>
                  <FormattedMessage id="pages.Search.resetFilters" />
                </Button>
              </ActionsWrapper>
            )}
          </NoResults>
        )}
      </SearchWrapper>
    </Wrapper>
  );
};

SearchOnline.propTypes = {
  visitedProviders: PropTypes.object,
  results: PropTypes.array,
  metadata: PropTypes.object,
  center: PropTypes.object,
  term: PropTypes.string,
  address: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  match: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,

  /* Dispatchable actions*/
  searchCourses: PropTypes.func.isRequired,
  searchByText: PropTypes.func.isRequired,
  changeAddress: PropTypes.func.isRequired,
  clearAddress: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  setFilter: PropTypes.func.isRequired,
};

SearchOnline.defaultProps = {
  visitedProviders: null,
  results: [],
  metadata: null,
  term: "",
  center: null,
  address: null,
};

export default injectIntl(SearchOnline);
